import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { useGetContactsQuery } from '../../../../../redux/services/contacts/contactsApi';
import { useAttachRealtyContactMutation } from '../../../../../redux/services/realties/realtiesApi';

import { SelectInput } from '../../../inputs/SelectInput';
import { AddContactForm } from '../../realtyForm/AddContactForm';
import { ButtonTemplate } from '../../../../ui/buttons/ButtonTemplate';
import { Dialog } from '../../../../ui/Dialog';
import { ErrorMsg } from '../../../../ui/ErrorMsg';

import { errorMessages } from '../../../../../data/constants';
import {
  handleResponseError,
  transformContactsData,
} from '../../../../../utils/data';
import { showError } from '../../../../../utils/ui';

const OWNER_ROLE = 1;

// Someone else's listing claimed without the owner's contact: linking it takes the listing over
export const AddOwnerContactForm = ({ realtyId, handleClose = () => {} }) => {
  const [contactId, setContactId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const userToken = useSelector(selectUserToken);
  const {
    data: contacts = [],
    isLoading: isContactsLoading,
    error: contactsError,
  } = useGetContactsQuery({ token: userToken }, { skip: !userToken });
  const [attachContact, { isLoading }] = useAttachRealtyContactMutation();

  const contactOptions = useMemo(
    () => transformContactsData(contacts),
    [contacts]
  );

  const selectedContact = useMemo(
    () => contactOptions.filter(option => option.value === contactId),
    [contactOptions, contactId]
  );

  const handleSubmit = async () => {
    const response = await attachContact({
      token: userToken,
      id: realtyId,
      contactId,
      role: OWNER_ROLE,
    });

    if (response?.error) {
      showError(handleResponseError(response.error));
      return;
    }

    toast.success('Owner contact added, the listing is now yours');
    handleClose();
  };

  return (
    <div className="flex flex-col gap-4 w-full sm:w-[420px]">
      <span className="text-base font-semibold text-blackColor">
        Add the owner’s contact
      </span>

      <span className="text-sm text-gray-600">
        The listing was claimed without the owner’s contact. Linking it moves
        the listing to you; the current agent is notified.
      </span>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 w-full">
          <SelectInput
            options={contactOptions}
            initialSelected={selectedContact}
            onSelect={([item]) => setContactId(item.value)}
            placeholder="Search contact"
            isSearchable={true}
            isDisabled={isContactsLoading}
          />

          <button
            type="button"
            title="New contact"
            className="flex flex-none items-center justify-center w-8 h-8 rounded text-[#444] hover:bg-[#f0f1f8] hover:text-[#5b63a8]"
            onClick={() => setIsAddOpen(true)}
          >
            <Plus size={20} />
          </button>
        </div>

        {contactsError && <ErrorMsg message={errorMessages.contacts} />}
      </div>

      <ButtonTemplate
        text="Add as owner"
        handleClick={handleSubmit}
        disabled={!contactId}
        isLoadingData={isLoading}
        bgColor={'bg-purpleColor border-purpleColor'}
      />

      {isAddOpen && (
        <Dialog
          content={
            <AddContactForm
              onCreated={setContactId}
              handleClose={() => setIsAddOpen(false)}
            />
          }
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
        />
      )}
    </div>
  );
};
